import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Email as EmailIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, ArrowBack as ArrowBackIcon, Person as PersonIcon } from '@mui/icons-material';
import { useAuthStore } from '@store/authStore';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('');
  const [autoRedirect, setAutoRedirect] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid or missing verification token.');
      setIsLoading(false);
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        setMessage(result.message || 'Your email has been verified successfully!');
        toast.success('Your email has been verified successfully!', { autoClose: 5000 });
        
        // Auto-redirect after 5 seconds if user is logged in (give time to see the message)
        if (isAuthenticated && user) {
          setAutoRedirect(true);
          setTimeout(() => {
            // Redirect to appropriate dashboard based on role
            if (user.role === 'player') {
              navigate('/player?emailVerified=true');
            } else if (user.role === 'coach') {
              navigate('/coach?emailVerified=true');
            } else {
              navigate('/dashboard?emailVerified=true');
            }
          }, 5000);
        }
      } else {
        setStatus('error');
        setMessage(result.message || 'Failed to verify email');
        toast.error(result.message || 'Failed to verify email');
      }
    } catch (error: any) {
      console.error('Verify email error:', error);
      setStatus('error');
      setMessage('An error occurred while verifying your email. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {isLoading ? (
              <>
                <CircularProgress size={48} sx={{ mb: 2 }} />
                <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                  Verifying Email...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while we verify your email address.
                </Typography>
              </>
            ) : status === 'success' ? (
              <>
                <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
                <Typography component="h1" variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Email Verified Successfully!
                </Typography>
                <Alert severity="success" sx={{ width: '100%', mb: 3, fontSize: '1.1rem', py: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {message}
                  </Typography>
                </Alert>
                {autoRedirect && isAuthenticated ? (
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Redirecting to your dashboard in a few seconds...
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<PersonIcon />}
                      onClick={() => {
                        if (user?.role === 'player') {
                          navigate('/player?emailVerified=true');
                        } else if (user?.role === 'coach') {
                          navigate('/coach?emailVerified=true');
                        } else {
                          navigate('/dashboard?emailVerified=true');
                        }
                      }}
                      sx={{ mb: 2 }}
                    >
                      Go to Dashboard Now
                    </Button>
                  </Box>
                ) : (
                  <>
                    {isAuthenticated ? (
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<PersonIcon />}
                        onClick={() => {
                          if (user?.role === 'player') {
                            navigate('/player?emailVerified=true');
                          } else if (user?.role === 'coach') {
                            navigate('/coach?emailVerified=true');
                          } else {
                            navigate('/dashboard?emailVerified=true');
                          }
                        }}
                        sx={{ mb: 2 }}
                      >
                        Go to Dashboard
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{ mb: 2 }}
                      >
                        Go to Login
                      </Button>
                    )}
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate('/')}
                    >
                      Back to Home
                    </Button>
                  </>
                )}
              </>
            ) : (
              <>
                <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
                <Typography component="h1" variant="h5" sx={{ mb: 2 }}>
                  Verification Failed
                </Typography>
                <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
                  {message}
                </Alert>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/login')}
                >
                  Back to Login
                </Button>
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailPage;

